<template>
  <div class="h-screen w-screen relative">
    <FadeSlideUp>
      <div v-if="ready" class="metrics absolute z-10" style="top: 24px; left: 24px;">
        <div class="w-full rounded-t-md bg-grey-500 py-4 px-6">
          <p class="text-white text-2xl font-header font-bold">Jetfly Action Against Covid-19</p>
          <p class="mt-2 xl:mt-4 text-white text-lg font-header">{{ currentDate }}</p>
        </div>
        <ul class="bg-white pt-6 pb-4 px-6 rounded-b-md flex xl:flex-col">
          <li v-for="(metric, i) in currentMetrics" :key="metric.key" :class="{ 'ml-6 xl:ml-0 xl:mt-4': i }" style="min-width: 130px">
            <div class="flex items-center text-grey-500">
              <component class="h-5 w-5" :is="metric.key" />
              <p class="ml-1 uppercase">{{ metric.label }}</p>
            </div>
            <p class="text-blue font-header font-bold text-3xl"><TweenedNumber :value="metric.value" /></p>
          </li>
        </ul>
      </div>
    </FadeSlideUp>
    <MglMap :accessToken="accessToken" :mapStyle.sync="mapStyle" class="map" @load="handleLoaded" />
  </div>
</template>

<script>
import 'mapbox-gl/dist/mapbox-gl.css'
import Mapbox from 'mapbox-gl'
import { MglMap } from 'vue-mapbox'
import turfLineChunk from '@turf/line-chunk'
import { format } from 'date-fns'
import timeout from '@/utils/timeout'
import FadeSlideUp from '@/components/transitions/FadeSlideUp'
import TweenedNumber from '@/components/flights-replay/TweenedNumber'
import masks from '@/components/svg/metrics/masks'
import flightTime from '@/components/svg/metrics/flightTime'
import pax from '@/components/svg/metrics/pax'

export default {
  components: { MglMap, FadeSlideUp, TweenedNumber, masks, flightTime, pax },
  props: {
    axiosRequest: { type: Function, required: true },
  },
  data () {
    return {
      accessToken: process.env.MIX_MAPBOX_ACCESS_TOKEN,
      mapStyle: process.env.MIX_MAPBOX_STYLE_URL,
      flights: [],
      metrics: [],
      mapLoaded: false,
      currentFlightIndex: null,
    }
  },
  computed: {
    ready () {
      return this.flights.length && this.mapLoaded
    },
    currentDate () {
      return format(new Date(this.flights[this.currentFlightIndex].date), 'dd/MM/u')
    },
    currentMetrics () {
      const metrics = {}
      for (const key in this.metrics) {
        metrics[key] = { ...this.metrics[key], value: 0, key }
      }

      for (const [i, flight] of this.flights.entries()) {
        if (i > this.currentFlightIndex) break
        for (const key in flight.metrics) {
          const val = key === 'flightTime' ? (flight.metrics[key] / 60) : flight.metrics[key]
          metrics[key].value = metrics[key].value += val
        }
      }

      return Object.keys(metrics).map(key => {
        return { ...metrics[key] }
      })
    },
  },
  watch: {
    async ready (val) {
      if (!val) return
      this.start()
    }
  },
  async mounted () {
    const { data: { features: flights, metrics } } = await this.axiosRequest()

    const m = {}
    for (const key in metrics) {
      m[key] = { label: metrics[key] }
    }
    this.metrics = m

    this.flights = flights.map(flight => {
      return {
        from: flight.geometry.coordinates[0],
        to: flight.geometry.coordinates[1],
        date: flight.properties.date,
        metrics: flight.properties.metrics,
      }
    })
  },
  methods: {
    async handleLoaded ({ map, component }) {
      this.map = map
      this.mapLoaded = true
    },
    async start () {
      for (const [index, flight] of this.flights.entries()) {
        this.currentFlightIndex = index
        // Next line sets delay before starting the animation
        if (index === 0) await timeout(3000)
        this.drawLine(index, flight.from, flight.to)
        await timeout(400)
      }
    },
    drawLine (index, fromCoordinates, toCoordinates) {
      const geojson = {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: [] }
        }]
      }

      this.map.addSource(`line-${index}`, {
        type: 'geojson',
        data: geojson,
      })

      // add the line which will be modified in the animation
      this.map.addLayer({
        id: `line-animation-${index}`,
        type: 'line',
        source: `line-${index}`,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#57ACE9', 'line-width': 2 }
      })

      const line = { type: 'LineString', coordinates: [fromCoordinates, toCoordinates] }
      const { features } = turfLineChunk(line, 15, { unit: 'miles' })
      const coordinates = features.reduce((acc, curr, i) => {
        if (i === 0) acc.push(curr.geometry.coordinates[0])
        acc.push(curr.geometry.coordinates[1])
        return acc
      }, [])

      let currentIndex = 0
      let animation

      const animateLine = async () => {
        if (currentIndex === 0) {
          geojson.features[0].geometry.coordinates[0] = coordinates[currentIndex]
          this.drawDot(coordinates[currentIndex])
        } else {
          geojson.features[0].geometry.coordinates[1] = coordinates[currentIndex]
        }

        if (currentIndex === coordinates.length) {
          this.drawDot(coordinates[currentIndex - 1])
          return
        }
        await timeout(20)
        this.map.getSource(`line-${index}`).setData(geojson)
        animation = requestAnimationFrame(animateLine)
        currentIndex++
      }

      animateLine()
    },
    drawDot (coordinates) {
      if (this.map.getSource('points')) {
        const geojson = this.map.getSource('points')._data
        const pointAlreadyDrawn = geojson.features.find(f => {
          return f.geometry.coordinates[0] === coordinates[0] && f.geometry.coordinates[1] === coordinates[1]
        })
        if (!pointAlreadyDrawn) {
          geojson.features.push({ type: 'Feature', geometry: { type: 'Point', coordinates } })
          this.map.getSource('points').setData(geojson)
        }
        this.map.removeLayer('dots')
      } else {
        this.map.addSource('points', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [{ type: 'Feature', geometry: { type: 'Point', coordinates } }]
          }
        })
      }
      this.map.addLayer({
        id: 'dots',
        source: 'points',
        type: 'circle',
        paint: { 'circle-color': '#333', 'circle-radius': 3 }
      })
    }
  },
}
</script>

<style scoped>
.map >>> canvas:focus {
  @apply outline-none;
}

.map >>> .mapboxgl-popup-tip {
  display: none;
}

.map >>> .mapboxgl-popup-content {
  padding: 0;
  background: none;
  box-shadow: none;
}

@media screen and (min-width: 1280px) {
  .metrics {
    width: 268px;
  }
}

</style>
